/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/ClockInConfirmScreen.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [4] clock_in_confirm — màn xác nhận riêng
 *          (ảnh vừa chụp + badge khoảng cách ✓/✗ + cảnh báo trễ/sớm/ngoài bán
 *          kính, KHÔNG chặn) trước khi thật sự gửi chấm công. Trước đây web
 *          gộp bước này chung với [1] home, không có màn riêng.
 *          2026-09-04 (structural layout parity, root causes #1/#2): mobile's
 *          renderClockInConfirm returns a bare fragment (no Card wrap), has no
 *          in-body title (AppHeader supplies it), and has NO "change method"
 *          button — back to method_select is ONLY via the header back button
 *          (headerConfig.clock_in_confirm.onBack). Removed the `.card`
 *          wrapper, the duplicate `<h2>` title, and the "‹ Đổi phương thức"
 *          button (+ its `onChangeMethod` prop) now that
 *          AttendanceModern.tsx's page header is state-driven and wires its
 *          back button to 'method_select' for this screen — no functionality
 *          lost, just no longer duplicated.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [4]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx renderClockInConfirm (:841-908)
 *  */

import React from 'react';
import styles from '@/pages/AttendanceModern.module.css';

interface ClockInConfirmScreenProps {
  photoBase64: string | null;
  geofenceResult: { distance: number; within: boolean } | null;
  timingWarning: { isLate: boolean; lateMinutes: number; isEarly: boolean; earlyMinutes: number };
  earlyClockinToleranceMinutes: number;
  maxClockInDistanceMeters: number | null;
  loading: boolean;
  errorMessage: string | null;
  accuracyInfo: { isValid: boolean; accuracy: number; threshold: number } | null;
  canOverrideAccuracy: boolean;
  onOverrideAccuracy: () => void;
  onRetakePhoto: () => void;
  onConfirm: () => void;
  onUseTimemark: () => void;
}

export const ClockInConfirmScreen: React.FC<ClockInConfirmScreenProps> = ({
  photoBase64,
  geofenceResult,
  timingWarning,
  earlyClockinToleranceMinutes,
  maxClockInDistanceMeters,
  loading,
  errorMessage,
  accuracyInfo,
  canOverrideAccuracy,
  onOverrideAccuracy,
  onRetakePhoto,
  onConfirm,
  onUseTimemark,
}) => {
  return (
    <>
      {photoBase64 && (
        <div className={styles.photoPreview}>
          <img src={photoBase64} alt="Ảnh vừa chụp" className={styles.photoImage} />
        </div>
      )}
      <div className={styles.actionsRow}>
        <button type="button" className={styles.secondaryButton} onClick={onRetakePhoto} data-testid="clock-in-confirm-retake">
          Chụp lại ảnh
        </button>
      </div>

      {geofenceResult && (
        <p
          className={`${styles.accuracyMessage} ${geofenceResult.within ? styles.accuracyValid : styles.accuracyInvalid}`}
          data-testid="clock-in-confirm-distance-badge"
        >
          {geofenceResult.within ? '✓' : '✗'} {Math.round(geofenceResult.distance)}m
        </p>
      )}
      {geofenceResult && !geofenceResult.within && (
        <p role="status" className={styles.timingWarningBanner} data-testid="clock-in-confirm-out-of-range">
          ⚠️ Bạn cách chi nhánh {Math.round(geofenceResult.distance)}m
          {maxClockInDistanceMeters != null ? ` (>${maxClockInDistanceMeters}m)` : ''} — vẫn chấm công được, bản ghi sẽ đánh dấu lệch vị trí.
        </p>
      )}
      {timingWarning.isLate && (
        <p role="status" className={styles.timingWarningBanner} data-testid="late-warning-banner">
          ⚠️ Bạn đang trễ {timingWarning.lateMinutes} phút so với giờ vào ca
        </p>
      )}
      {timingWarning.isEarly && (
        <p role="status" className={styles.timingWarningBanner} data-testid="early-warning-banner">
          ⚠️ Bạn đang chấm sớm {timingWarning.earlyMinutes} phút trước giờ vào ca (giới hạn {earlyClockinToleranceMinutes} phút)
        </p>
      )}
      {errorMessage && (
        <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert">
          {errorMessage}
        </p>
      )}
      {accuracyInfo && !accuracyInfo.isValid && canOverrideAccuracy && (
        <div className={styles.overrideRow}>
          <button type="button" className={styles.overrideButton} onClick={onOverrideAccuracy}>
            Bỏ qua kiểm tra GPS (Quản trị)
          </button>
          <span className={styles.overrideNote}>Chỉ sử dụng khi tín hiệu GPS yếu và có xác minh thực tế.</span>
        </div>
      )}

      <div className={styles.actionsRow}>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onConfirm}
          disabled={loading || !photoBase64}
          data-testid="clock-in-confirm-btn"
        >
          Xác nhận chấm công vào
        </button>
        {geofenceResult && !geofenceResult.within && (
          <button type="button" className={styles.secondaryButton} onClick={onUseTimemark} data-testid="clock-in-confirm-use-timemark">
            Dùng Timemark (cần duyệt)
          </button>
        )}
      </div>
    </>
  );
};

export default ClockInConfirmScreen;
